import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Award, Target, TrendingUp, CheckCircle2, Clock, AlertCircle, Plus,
  Users, Sparkles, Calendar, Edit3, Send, Briefcase, Star, X,
} from "lucide-react";
import DateInputBR from "@/components/DateInputBR";
import { fmtDate } from "@/lib/format";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { toast } from "sonner";

const STATUS_LABEL = {
  pendente: { label: "Pendente", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  em_andamento: { label: "Em andamento", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  concluido: { label: "Concluído", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  vencido: { label: "Vencido", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  ativo: { label: "Ativo", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  rascunho: { label: "Rascunho", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  cancelado: { label: "Cancelado", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  planejado: { label: "Planejado", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  encerrado: { label: "Encerrado", cls: "bg-slate-200 text-slate-700 border-slate-300" },
};

export default function AppraisalsPage() {
  const { companyParam, companies } = useCompany();
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [pdiDash, setPdiDash] = useState(null);
  const [cycles, setCycles] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyParam) params.company_id = companyParam;
      const [{ data: d1 }, { data: d2 }, { data: d3 }] = await Promise.all([
        api.get("/appraisals/dashboard", { params }),
        api.get("/pdis/dashboard", { params }),
        api.get("/appraisal-cycles", { params }),
      ]);
      setDash(d1); setPdiDash(d2); setCycles(d3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [companyParam]);

  return (
    <div className="space-y-5 pb-10" data-testid="appraisals-page">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-2xl border border-[#C5A059]/25 bg-gradient-to-br from-[#234935] via-[#1C3A2B] to-[#0F1C14] p-5 sm:p-6 text-white">
        <div aria-hidden className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#C5A059]/20 blur-[80px]" />
        <div className="relative">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C5A059] font-medium flex items-center gap-1.5">
            <Award className="h-3 w-3" /> Avaliação & Desenvolvimento
          </div>
          <h1 className="mt-1 font-display text-[28px] leading-tight sm:text-[34px] font-semibold tracking-tight">
            Avaliação de Desempenho · PDI
          </h1>
          <p className="mt-1.5 text-[12.5px] sm:text-[13px] text-white/70 max-w-[680px]">
            Ciclos, autoavaliação, avaliação do gestor, planos de desenvolvimento e indicadores estratégicos de pessoas.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-white border border-border h-10 p-1 rounded-lg w-full sm:w-auto inline-flex flex-wrap" data-testid="appr-tabs">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="data-[state=active]:bg-[#234935] data-[state=active]:text-white">Dashboard</TabsTrigger>
          <TabsTrigger value="cycles" data-testid="tab-cycles" className="data-[state=active]:bg-[#234935] data-[state=active]:text-white">Ciclos</TabsTrigger>
          <TabsTrigger value="appraisals" data-testid="tab-appraisals" className="data-[state=active]:bg-[#234935] data-[state=active]:text-white">Avaliações</TabsTrigger>
          <TabsTrigger value="pdis" data-testid="tab-pdis" className="data-[state=active]:bg-[#234935] data-[state=active]:text-white">PDIs</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <DashboardTab loading={loading} dash={dash} pdiDash={pdiDash} />
        </TabsContent>
        <TabsContent value="cycles">
          <CyclesTab cycles={cycles} reload={reload} companies={companies} loading={loading} />
        </TabsContent>
        <TabsContent value="appraisals">
          <AppraisalsTab cycles={cycles} companies={companies} companyParam={companyParam} />
        </TabsContent>
        <TabsContent value="pdis">
          <PdisTab cycles={cycles} companies={companies} companyParam={companyParam} reload={reload} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================ DASHBOARD ============================
function DashboardTab({ loading, dash, pdiDash }) {
  if (loading || !dash || !pdiDash) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={`sk-${i}`} className="h-[110px]" />)}
      </div>
    );
  }
  const t = dash.totals;
  const pt = pdiDash.totals;
  return (
    <div className="space-y-4" data-testid="appr-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Avaliações" value={t.total} sub={`${t.pendente} pendentes`} icon={Award} accent="emerald" testId="dash-total" />
        <KpiCard label="Concluídas" value={t.concluido} sub={`${t.pct_concluido}% do ciclo`} icon={CheckCircle2} accent="emerald" testId="dash-concluido" />
        <KpiCard label="Nota média" value={t.avg_score?.toFixed(2) ?? "—"} sub="Escala 1-5" icon={Star} accent="ochre" testId="dash-avg" />
        <KpiCard label="PDIs ativos" value={pt.ativos} sub={`${pt.actions_vencidas} ações vencidas`} icon={Target} accent="ochre" testId="dash-pdis" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Score distribution */}
        <div className="rounded-xl border border-border bg-white p-4 lg:col-span-2" data-testid="dash-distribution">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8A6B2B] font-semibold mb-1">Distribuição de notas</div>
          <div className="font-display text-lg font-semibold mb-3">Performance do ciclo</div>
          <div className="h-[230px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dash.score_distribution} margin={{ top: 5, right: 8, bottom: 5, left: -8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DD" vertical={false} />
                <XAxis dataKey="range" tick={{ fontSize: 10.5, fill: "#374151" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} width={28} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="count" fill="#234935" radius={[6, 6, 0, 0]} name="Avaliações" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Progress trio */}
        <div className="rounded-xl border border-border bg-white p-4 space-y-3" data-testid="dash-progress">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8A6B2B] font-semibold">Progresso</div>
            <div className="font-display text-lg font-semibold">Conclusão do ciclo</div>
          </div>
          <Progress label="Autoavaliação" pct={t.pct_self_concluido} />
          <Progress label="Avaliação do gestor" pct={t.pct_manager_concluido} />
          <Progress label="Concluído (ambos)" pct={t.pct_concluido} accent />
          <hr className="border-border" />
          <Progress label="Ações de PDI concluídas" pct={pt.pct_acoes_concluidas} />
          <div className="text-[11px] text-rose-700 flex items-center gap-1.5">
            <AlertCircle className="h-3 w-3" /> {pt.actions_vencidas} ações vencidas em PDIs
          </div>
        </div>
      </div>

      {/* By sector */}
      {dash.by_sector.length > 0 && (
        <div className="rounded-xl border border-border bg-white p-4" data-testid="dash-by-sector">
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#8A6B2B] font-semibold mb-1">Por setor</div>
          <div className="font-display text-lg font-semibold mb-3">Performance &amp; conclusão por setor</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="bg-slate-50 border-b border-border">
                  <th className="text-left px-3 py-2">Setor</th>
                  <th className="text-right px-3 py-2 w-[100px]">Total</th>
                  <th className="text-right px-3 py-2 w-[110px]">Concluídas</th>
                  <th className="text-right px-3 py-2 w-[100px]">% Concl.</th>
                  <th className="text-right px-3 py-2 w-[110px]">Nota média</th>
                </tr>
              </thead>
              <tbody>
                {dash.by_sector.map((s) => (
                  <tr key={s.sector_id || s.sector_name} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-medium">{s.sector_name}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.total}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{s.concluidas}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{s.pct}%</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#234935]">{s.avg_score ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================ CYCLES ============================
function CyclesTab({ cycles, reload, companies, loading }) {
  return (
    <div className="space-y-4" data-testid="cycles-tab">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[12.5px] text-muted-foreground">{cycles.length} ciclo(s) cadastrado(s)</div>
        <CycleFormDialog onSaved={reload} companies={companies} />
      </div>
      {loading ? (
        <div className="grid gap-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={`sk-${i}`} className="h-[120px]" />)}
        </div>
      ) : cycles.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center text-[13px] text-muted-foreground">
          Nenhum ciclo de avaliação cadastrado. Clique em <b>Novo ciclo</b> para começar.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {cycles.map((c) => (
            <CycleCard key={c.id} cycle={c} onChange={reload} />
          ))}
        </div>
      )}
    </div>
  );
}

function CycleCard({ cycle, onChange }) {
  const [launching, setLaunching] = useState(false);
  const launch = async () => {
    setLaunching(true);
    try {
      const { data } = await api.post(`/appraisal-cycles/${cycle.id}/launch`);
      toast.success(`Ciclo iniciado: ${data.created} avaliações criadas, ${data.skipped} já existiam.`);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLaunching(false); }
  };
  const close = async () => {
    if (!window.confirm("Encerrar este ciclo?")) return;
    try {
      await api.post(`/appraisal-cycles/${cycle.id}/close`);
      toast.success("Ciclo encerrado.");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const status = STATUS_LABEL[cycle.status] || STATUS_LABEL.planejado;
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={`cycle-${cycle.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-display text-[16px] font-semibold">{cycle.name}</div>
          <div className="text-[11.5px] text-muted-foreground mt-0.5">
            <Calendar className="inline h-3 w-3 mr-1" />
            {cycle.start_date} → {cycle.end_date}
          </div>
        </div>
        <Badge variant="outline" className={`text-[10px] ${status.cls}`}>{status.label}</Badge>
      </div>
      {cycle.description && <p className="text-[12.5px] text-slate-700 mb-2">{cycle.description}</p>}
      <div className="grid grid-cols-3 gap-2 text-center my-2">
        <Mini label="Avaliações" value={cycle.appraisals_total || 0} />
        <Mini label="Concluídas" value={cycle.appraisals_concluidas || 0} />
        <Mini label="% Concl." value={`${cycle.appraisals_pct || 0}%`} accent />
      </div>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        {cycle.status === "planejado" && (
          <Button size="sm" onClick={launch} disabled={launching}
                  className="bg-[#234935] hover:bg-[#2c5c43] text-white"
                  data-testid={`launch-${cycle.id}`}>
            <Send className={`h-3 w-3 mr-1 ${launching ? "animate-pulse" : ""}`} /> Iniciar ciclo
          </Button>
        )}
        {cycle.status === "ativo" && (
          <Button size="sm" variant="outline" onClick={close} data-testid={`close-${cycle.id}`}>
            Encerrar ciclo
          </Button>
        )}
        <CycleFormDialog cycle={cycle} onSaved={onChange} companies={null} />
      </div>
    </div>
  );
}

function CycleFormDialog({ cycle, onSaved, companies }) {
  const [open, setOpen] = useState(false);
  const isEdit = !!cycle;
  const [form, setForm] = useState(() => ({
    name: cycle?.name || "",
    description: cycle?.description || "",
    company_id: cycle?.company_id || "",
    start_date: cycle?.start_date || new Date().toISOString().slice(0, 10),
    end_date: cycle?.end_date || "",
    include_self_appraisal: cycle?.include_self_appraisal ?? true,
    include_manager_appraisal: cycle?.include_manager_appraisal ?? true,
  }));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name || !form.start_date || !form.end_date) {
      toast.error("Preencha nome e datas");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        company_id: form.company_id || null,
        competencies: cycle?.competencies || [],
      };
      if (isEdit) {
        await api.put(`/appraisal-cycles/${cycle.id}`, payload);
        toast.success("Ciclo atualizado.");
      } else {
        await api.post("/appraisal-cycles", payload);
        toast.success("Ciclo criado.");
      }
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant={isEdit ? "outline" : "default"}
                className={isEdit ? "" : "bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]"}
                data-testid={isEdit ? `edit-cycle-${cycle.id}` : "new-cycle-btn"}>
          {isEdit ? <Edit3 className="h-3.5 w-3.5 mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
          {isEdit ? "Editar" : "Novo ciclo"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[560px]" aria-describedby="cycle-form-desc">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar ciclo" : "Novo ciclo de avaliação"}</DialogTitle>
        </DialogHeader>
        <p id="cycle-form-desc" className="text-[12px] text-muted-foreground">
          Defina o nome, escopo e período. Ao iniciar, o sistema cria avaliações para todos colaboradores ativos do escopo.
        </p>
        <div className="grid grid-cols-1 gap-3 mt-2">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Nome do ciclo</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Ex.: Avaliação 2026 Q2" data-testid="cycle-name" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Descrição</Label>
            <Textarea value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Objetivos do ciclo" rows={2} data-testid="cycle-desc" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Início</Label>
              <DateInputBR value={form.start_date}
                     onChange={(v) => setForm({ ...form, start_date: v })}
                     testId="cycle-start" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Encerramento</Label>
              <DateInputBR value={form.end_date}
                     onChange={(v) => setForm({ ...form, end_date: v })}
                     testId="cycle-end" />
            </div>
          </div>
          {companies && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Empresa (opcional)</Label>
              <Select value={form.company_id || "all"}
                      onValueChange={(v) => setForm({ ...form, company_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="cycle-company"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas (Grupo)</SelectItem>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 mt-1">
            <label className="flex items-center gap-2 text-[12.5px] cursor-pointer">
              <input type="checkbox" checked={form.include_self_appraisal}
                     onChange={(e) => setForm({ ...form, include_self_appraisal: e.target.checked })}
                     className="accent-[#C5A059]" />
              Incluir autoavaliação
            </label>
            <label className="flex items-center gap-2 text-[12.5px] cursor-pointer">
              <input type="checkbox" checked={form.include_manager_appraisal}
                     onChange={(e) => setForm({ ...form, include_manager_appraisal: e.target.checked })}
                     className="accent-[#C5A059]" />
              Incluir avaliação do gestor
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving}
                  className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]"
                  data-testid="cycle-save">
            {isEdit ? "Salvar" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================ APPRAISALS ============================
function AppraisalsTab({ cycles, companies, companyParam }) {
  const [cycleId, setCycleId] = useState(cycles[0]?.id || "");
  const [statusF, setStatusF] = useState("all");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!cycleId && cycles.length) setCycleId(cycles[0].id);
  }, [cycles, cycleId]);

  const load = async () => {
    if (!cycleId) return;
    setLoading(true);
    try {
      const params = { cycle_id: cycleId };
      if (statusF !== "all") params.status = statusF;
      if (companyParam) params.company_id = companyParam;
      const { data } = await api.get("/appraisals", { params });
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [cycleId, statusF, companyParam]);

  return (
    <div className="space-y-4" data-testid="appraisals-tab">
      <div className="rounded-xl border border-border bg-white p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider">Ciclo</Label>
          <Select value={cycleId} onValueChange={setCycleId}>
            <SelectTrigger data-testid="appr-filter-cycle"><SelectValue placeholder="Selecione" /></SelectTrigger>
            <SelectContent>
              {cycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider">Status</Label>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger data-testid="appr-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="em_andamento">Em andamento</SelectItem>
              <SelectItem value="concluido">Concluído</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          <div className="text-[12.5px] text-muted-foreground">{list.length} avaliação(ões)</div>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-[300px]" />
      ) : list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center text-[13px] text-muted-foreground">
          Nenhuma avaliação encontrada. Inicie o ciclo na aba <b>Ciclos</b>.
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-white overflow-hidden" data-testid="appr-table">
          <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="bg-[#234935] text-white sticky top-0">
                  <th className="text-left px-3 py-2 font-semibold">Colaborador</th>
                  <th className="text-left px-3 py-2 font-semibold">Setor</th>
                  <th className="text-left px-3 py-2 font-semibold">Cargo</th>
                  <th className="text-center px-3 py-2 font-semibold">Auto</th>
                  <th className="text-center px-3 py-2 font-semibold">Gestor</th>
                  <th className="text-center px-3 py-2 font-semibold">Final</th>
                  <th className="text-left px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((a, idx) => {
                  const st = STATUS_LABEL[a.status] || STATUS_LABEL.pendente;
                  return (
                    <tr key={a.id} className={idx % 2 ? "bg-slate-50/50" : ""}>
                      <td className="px-3 py-1.5 font-medium">{a.employee_name}</td>
                      <td className="px-3 py-1.5">{a.sector_name || "—"}</td>
                      <td className="px-3 py-1.5">{a.cargo || "—"}</td>
                      <td className="px-3 py-1.5 text-center font-mono tabular-nums">
                        {a.self_final_score?.toFixed(2) ?? "—"}
                      </td>
                      <td className="px-3 py-1.5 text-center font-mono tabular-nums">
                        {a.manager_final_score?.toFixed(2) ?? "—"}
                      </td>
                      <td className="px-3 py-1.5 text-center font-mono tabular-nums font-semibold text-[#234935]">
                        {a.final_score?.toFixed(2) ?? "—"}
                      </td>
                      <td className="px-3 py-1.5">
                        <Badge variant="outline" className={`text-[10px] ${st.cls}`}>{st.label}</Badge>
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <Button size="sm" variant="ghost"
                                onClick={() => setActive(a)}
                                data-testid={`appr-open-${a.id}`}>
                          Avaliar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {active && <AppraisalDialog appraisal={active} onClose={() => setActive(null)} onSaved={load} />}
    </div>
  );
}

function AppraisalDialog({ appraisal, onClose, onSaved }) {
  const [tab, setTab] = useState("manager");
  const [data, setData] = useState(null);
  const [scores, setScores] = useState({});
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/appraisals/${appraisal.id}`).then(({ data: d }) => setData(d));
  }, [appraisal.id]);

  useEffect(() => {
    if (!data) return;
    if (tab === "manager") {
      setScores(data.manager_scores || {});
      setComment(data.manager_comment || "");
    } else {
      setScores(data.self_scores || {});
      setComment(data.self_comment || "");
    }
  }, [tab, data]);

  const submit = async () => {
    setSaving(true);
    try {
      const url = `/appraisals/${appraisal.id}/${tab === "manager" ? "manager" : "self"}`;
      const { data: d } = await api.post(url, { scores, comment });
      setData(d);
      toast.success(`${tab === "manager" ? "Avaliação do gestor" : "Autoavaliação"} salva.`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const competencies = data?.cycle?.competencies || [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" aria-describedby="appr-dlg-desc">
        <DialogHeader>
          <DialogTitle>Avaliação · {appraisal.employee_name}</DialogTitle>
        </DialogHeader>
        <p id="appr-dlg-desc" className="text-[12px] text-muted-foreground">
          {appraisal.cargo} · {appraisal.sector_name}
        </p>
        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="manager" data-testid="appr-dlg-manager">Avaliação do gestor</TabsTrigger>
            <TabsTrigger value="self" data-testid="appr-dlg-self">Autoavaliação</TabsTrigger>
          </TabsList>
          <TabsContent value={tab}>
            <div className="space-y-3 mt-2">
              {competencies.map((c) => (
                <CompetencyRow
                  key={c.key}
                  competency={c}
                  value={scores[c.key]}
                  onChange={(v) => setScores({ ...scores, [c.key]: v })}
                />
              ))}
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Comentário</Label>
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)}
                          rows={3} placeholder="Observações, pontos fortes, oportunidades de desenvolvimento..."
                          data-testid="appr-comment" />
              </div>
            </div>
          </TabsContent>
        </Tabs>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button onClick={submit} disabled={saving}
                  className="bg-[#234935] hover:bg-[#2c5c43] text-white"
                  data-testid="appr-submit">
            <Send className={`h-3.5 w-3.5 mr-1.5 ${saving ? "animate-pulse" : ""}`} /> Submeter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompetencyRow({ competency, value, onChange }) {
  return (
    <div className="rounded-md border border-border p-2.5 bg-slate-50/40">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="font-medium text-[13px]">{competency.label}</div>
        <div className="text-[10.5px] text-muted-foreground">Peso {competency.weight}</div>
      </div>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`h-7 w-7 rounded-md text-[12px] font-semibold transition-all ${
              value === n
                ? "bg-[#234935] text-white shadow"
                : "bg-white border border-border text-slate-600 hover:bg-slate-100"
            }`}
            data-testid={`score-${competency.key}-${n}`}>
            {n}
          </button>
        ))}
        <div className="ml-2 text-[11px] text-muted-foreground">
          {value ? ["—", "Insatisfatório", "Em desenvolvimento", "Atende", "Supera", "Excepcional"][value] : "Selecione"}
        </div>
      </div>
    </div>
  );
}

// ============================ PDIs ============================
function PdisTab({ cycles, companies, companyParam, reload }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusF !== "all") params.status = statusF;
      if (companyParam) params.company_id = companyParam;
      const { data } = await api.get("/pdis", { params });
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusF, companyParam]);

  return (
    <div className="space-y-4" data-testid="pdis-tab">
      <div className="rounded-xl border border-border bg-white p-3 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Label className="text-[11px] uppercase tracking-wider">Status</Label>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger className="w-[180px]" data-testid="pdi-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="ativo">Ativos</SelectItem>
              <SelectItem value="rascunho">Rascunhos</SelectItem>
              <SelectItem value="concluido">Concluídos</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-[12.5px] text-muted-foreground">{list.length} PDI(s)</span>
        </div>
        <PdiFormDialog cycles={cycles} onSaved={() => { load(); reload(); }} />
      </div>
      {loading ? (
        <div className="grid gap-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={`sk-${i}`} className="h-[140px]" />)}
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center text-[13px] text-muted-foreground">
          Nenhum PDI cadastrado. Clique em <b>Novo PDI</b> para começar.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {list.map((p) => (
            <PdiCard key={p.id} pdi={p}
                     onEdit={() => setEditing(p)}
                     onChange={() => { load(); reload(); }} />
          ))}
        </div>
      )}
      {editing && (
        <PdiFormDialog
          pdi={editing} cycles={cycles}
          forceOpen
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); reload(); }} />
      )}
    </div>
  );
}

function PdiCard({ pdi, onEdit, onChange }) {
  const status = STATUS_LABEL[pdi.status] || STATUS_LABEL.ativo;
  const setStatus = async (action, newStatus) => {
    try {
      await api.post(`/pdis/${pdi.id}/actions/${action.id}/status`, { status: newStatus });
      toast.success("Status atualizado.");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  return (
    <div className="rounded-xl border border-border bg-white p-4" data-testid={`pdi-${pdi.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-display text-[15px] font-semibold truncate">{pdi.title}</div>
          <div className="text-[11.5px] text-muted-foreground">
            <Briefcase className="inline h-3 w-3 mr-1" />
            {pdi.employee_name} · {pdi.sector_name || "—"}
          </div>
        </div>
        <Badge variant="outline" className={`text-[10px] ${status.cls}`}>{status.label}</Badge>
      </div>
      {pdi.description && <p className="text-[12px] text-slate-700 mb-2 line-clamp-2">{pdi.description}</p>}
      <div className="grid grid-cols-3 gap-2 text-center my-2">
        <Mini label="Ações" value={pdi.actions_total || 0} />
        <Mini label="Concluídas" value={pdi.actions_concluidas || 0} accent />
        <Mini label="Vencidas" value={pdi.actions_vencidas || 0} tone={pdi.actions_vencidas > 0 ? "rose" : "muted"} />
      </div>
      <div className="space-y-1.5 mt-2">
        {(pdi.actions || []).slice(0, 4).map((a) => {
          const ast = STATUS_LABEL[a.status] || STATUS_LABEL.pendente;
          return (
            <div key={a.id} className="flex items-center gap-2 rounded border border-border bg-slate-50/50 px-2 py-1.5 text-[11.5px]">
              <span className="flex-1 truncate">{a.descricao}</span>
              {a.prazo && <span className="text-muted-foreground tabular-nums">{fmtDate(a.prazo)}</span>}
              <Select value={a.status} onValueChange={(v) => setStatus(a, v)}>
                <SelectTrigger className="h-7 w-[130px] text-[10.5px]" data-testid={`pdi-action-status-${a.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="em_andamento">Em andamento</SelectItem>
                  <SelectItem value="concluido">Concluído</SelectItem>
                </SelectContent>
              </Select>
              <Badge variant="outline" className={`text-[9.5px] ${ast.cls}`}>{ast.label}</Badge>
            </div>
          );
        })}
        {(pdi.actions || []).length > 4 && (
          <div className="text-[10.5px] text-muted-foreground">+ {pdi.actions.length - 4} ação(ões)</div>
        )}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={onEdit} data-testid={`pdi-edit-${pdi.id}`}>
          <Edit3 className="h-3 w-3 mr-1" /> Editar
        </Button>
      </div>
    </div>
  );
}

function PdiFormDialog({ pdi, cycles, onSaved, onClose, forceOpen }) {
  const [open, setOpen] = useState(forceOpen || false);
  const isEdit = !!pdi;
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(() => ({
    employee_id: pdi?.employee_id || "",
    cycle_id: pdi?.cycle_id || "",
    title: pdi?.title || "",
    description: pdi?.description || "",
    focus_competencies: pdi?.focus_competencies || [],
    actions: pdi?.actions || [],
    status: pdi?.status || "ativo",
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open || forceOpen) {
      api.get("/employees?page_size=2000").then(({ data }) => setEmployees(data.items || []));
    }
  }, [open, forceOpen]);

  const setOpenWrapped = (v) => {
    setOpen(v);
    if (!v && onClose) onClose();
  };

  const addAction = () => {
    setForm((f) => ({ ...f, actions: [...f.actions, { descricao: "", prazo: "", status: "pendente" }] }));
  };
  const updateAction = (idx, key, val) => {
    setForm((f) => {
      const next = [...f.actions];
      next[idx] = { ...next[idx], [key]: val };
      return { ...f, actions: next };
    });
  };
  const removeAction = (idx) => {
    setForm((f) => ({ ...f, actions: f.actions.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.employee_id || !form.title) {
      toast.error("Selecione colaborador e informe o título");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, cycle_id: form.cycle_id || null };
      if (isEdit) {
        await api.put(`/pdis/${pdi.id}`, payload);
        toast.success("PDI atualizado.");
      } else {
        await api.post("/pdis", payload);
        toast.success("PDI criado.");
      }
      setOpenWrapped(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={forceOpen || open} onOpenChange={setOpenWrapped}>
      {!isEdit && (
        <DialogTrigger asChild>
          <Button size="sm" className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]" data-testid="new-pdi-btn">
            <Plus className="h-3.5 w-3.5 mr-1" /> Novo PDI
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="sm:max-w-[680px] max-h-[90vh] overflow-y-auto" aria-describedby="pdi-form-desc">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar PDI" : "Novo PDI"}</DialogTitle>
        </DialogHeader>
        <p id="pdi-form-desc" className="text-[12px] text-muted-foreground">
          Plano de desenvolvimento individual com ações, prazos e responsáveis.
        </p>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Colaborador</Label>
              <Select value={form.employee_id}
                      onValueChange={(v) => setForm({ ...form, employee_id: v })}
                      disabled={isEdit}>
                <SelectTrigger data-testid="pdi-employee">
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.nome} · {e.cargo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Ciclo (opcional)</Label>
              <Select value={form.cycle_id || "none"}
                      onValueChange={(v) => setForm({ ...form, cycle_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="pdi-cycle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem ciclo</SelectItem>
                  {cycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Título</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                   placeholder="Ex.: Desenvolver liderança técnica" data-testid="pdi-title" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Descrição</Label>
            <Textarea value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      rows={2} data-testid="pdi-desc" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[11px] uppercase tracking-wider">Ações de desenvolvimento</Label>
              <Button size="sm" variant="outline" onClick={addAction} data-testid="pdi-add-action">
                <Plus className="h-3 w-3 mr-1" /> Adicionar ação
              </Button>
            </div>
            <div className="space-y-2">
              {form.actions.length === 0 && (
                <div className="text-[12px] text-muted-foreground italic px-2 py-3 text-center rounded-md border border-dashed border-border">
                  Nenhuma ação cadastrada. Clique em <b>Adicionar ação</b>.
                </div>
              )}
              {form.actions.map((a, idx) => (
                <div key={a.id || `new-${idx}`} className="rounded-md border border-border p-2.5 bg-slate-50/40 space-y-2" data-testid={`pdi-action-${idx}`}>
                  <div className="flex items-start gap-2">
                    <Input value={a.descricao}
                           onChange={(e) => updateAction(idx, "descricao", e.target.value)}
                           placeholder="Descrição da ação"
                           className="flex-1" />
                    <Button size="sm" variant="ghost"
                            onClick={() => removeAction(idx)}
                            className="text-rose-600">
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider">Prazo</Label>
                      <DateInputBR value={a.prazo || ""}
                             onChange={(v) => updateAction(idx, "prazo", v)} />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider">Status</Label>
                      <Select value={a.status || "pendente"}
                              onValueChange={(v) => updateAction(idx, "status", v)}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pendente">Pendente</SelectItem>
                          <SelectItem value="em_andamento">Em andamento</SelectItem>
                          <SelectItem value="concluido">Concluído</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Responsável (opcional)</Label>
                    <Input value={a.responsavel_nome || ""}
                           onChange={(e) => updateAction(idx, "responsavel_nome", e.target.value)}
                           placeholder="Nome do responsável" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Status do PDI</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="pdi-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rascunho">Rascunho</SelectItem>
                <SelectItem value="ativo">Ativo</SelectItem>
                <SelectItem value="concluido">Concluído</SelectItem>
                <SelectItem value="cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => setOpenWrapped(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving}
                  className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]"
                  data-testid="pdi-save">
            {isEdit ? "Salvar" : "Criar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================ SHARED ============================
function KpiCard({ label, value, sub, icon: Icon, accent = "emerald", testId }) {
  const a = accent === "ochre"
    ? { bar: "bg-[#C5A059]", iconBg: "bg-[#FAF4E5]", iconText: "text-[#8A6B2B]" }
    : { bar: "bg-emerald-500", iconBg: "bg-emerald-50", iconText: "text-emerald-600" };
  return (
    <div data-testid={testId}
         className="relative overflow-hidden rounded-xl border border-border bg-white p-3.5 sm:p-4 hover:shadow-md transition-shadow">
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${a.bar}`} />
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] sm:text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground font-medium">{label}</div>
          <div className="font-display text-2xl sm:text-3xl font-semibold tabular-nums mt-1 text-slate-900">{value}</div>
          <div className="text-[10.5px] sm:text-[11px] mt-1 text-muted-foreground">{sub}</div>
        </div>
        <div className={`grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-lg shrink-0 ${a.iconBg}`}>
          <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${a.iconText}`} />
        </div>
      </div>
    </div>
  );
}

function Mini({ label, value, accent, tone }) {
  const cls = accent
    ? "border-[#234935]/30 bg-[#234935]/5 text-[#234935]"
    : tone === "rose"
    ? "border-rose-200 bg-rose-50 text-rose-700"
    : "border-border bg-white text-slate-700";
  return (
    <div className={`rounded-md border p-1.5 ${cls}`}>
      <div className="text-[9px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-display text-[14px] font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Progress({ label, pct, accent }) {
  const safe = Math.min(100, Math.max(0, Number(pct) || 0));
  return (
    <div>
      <div className="flex items-center justify-between text-[11.5px] mb-1">
        <span className="text-slate-700">{label}</span>
        <span className="font-mono tabular-nums font-semibold">{safe.toFixed(1)}%</span>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${
          accent ? "bg-gradient-to-r from-[#234935] to-[#C5A059]" : "bg-[#234935]"
        }`} style={{ width: `${safe}%` }} />
      </div>
    </div>
  );
}
